/**
 * QAStats — live session statistics tracker for Rush Fever 7s Deluxe QA mode.
 * Tracks RTP, feature hits, win distribution, and recent spin history.
 */

import { QASpinResult } from './QAOverrides';

/** Win-size bucket names */
export type WinBucket = 'zero' | 'small' | 'medium' | 'big' | 'huge' | 'mega';

/** Feature-hit counters */
export interface FeatureHits {
  jackpotPick: number;
  freeGames: number;
  rushFever: number;
  bigWin: {
    amazing: number;
    awesome: number;
    incredible: number;
  };
}

/** Win-distribution counters (by bet multiplier) */
export interface WinDistribution {
  /** 0x */
  zero: number;
  /** 0.01–2x */
  small: number;
  /** 2–10x */
  medium: number;
  /** 10–25x */
  big: number;
  /** 25–50x */
  huge: number;
  /** 50x+ */
  mega: number;
}

/** Full session stats snapshot */
export interface SessionStats {
  totalSpins: number;
  totalBet: number;
  totalWin: number;
  rtp: number;
  featureHits: FeatureHits;
  winDistribution: WinDistribution;
  recentSpins: QASpinResult[];
  startTime: number;
  avgSpinTime: number;
}

const MAX_RECENT = 50;

const FEATURE_KEYS: readonly string[] = [
  'jackpotPick', 'freeGames', 'rushFever',
  'bigWinAmazing', 'bigWinAwesome', 'bigWinIncredible',
];

function emptyFeatureHits(): FeatureHits {
  return {
    jackpotPick: 0,
    freeGames: 0,
    rushFever: 0,
    bigWin: { amazing: 0, awesome: 0, incredible: 0 },
  };
}

function emptyDistribution(): WinDistribution {
  return { zero: 0, small: 0, medium: 0, big: 0, huge: 0, mega: 0 };
}

/**
 * Classifies a win amount into a distribution bucket based on bet multiplier.
 */
function classifyWin(totalWin: number, totalBet: number): WinBucket {
  if (totalBet <= 0 || totalWin <= 0) return 'zero';
  const mult = totalWin / totalBet;
  if (mult <= 2) return 'small';
  if (mult <= 10) return 'medium';
  if (mult <= 25) return 'big';
  if (mult <= 50) return 'huge';
  return 'mega';
}

export class QAStats {
  private _totalSpins = 0;
  private _totalBet = 0;
  private _totalWin = 0;
  private _featureHits: FeatureHits = emptyFeatureHits();
  private _winDistribution: WinDistribution = emptyDistribution();
  private _recentSpins: QASpinResult[] = [];
  private _startTime: number = Date.now();
  private _lastSpinTime: number = this._startTime;
  private _spinTimeSum = 0;

  /** Record a completed spin result. */
  recordSpin(result: QASpinResult): void {
    this._totalSpins++;
    this._totalBet += result.totalBet;
    this._totalWin += result.totalWin;

    // Spin timing
    const now = Date.now();
    if (this._totalSpins > 1) {
      this._spinTimeSum += now - this._lastSpinTime;
    }
    this._lastSpinTime = now;

    // Win distribution
    const bucket = classifyWin(result.totalWin, result.totalBet);
    this._winDistribution[bucket]++;

    // Feature tracking
    for (const feat of result.features) {
      if (feat === 'jackpotPick') this._featureHits.jackpotPick++;
      else if (feat === 'freeGames') this._featureHits.freeGames++;
      else if (feat === 'rushFever') this._featureHits.rushFever++;
      else if (feat === 'bigWinAmazing') this._featureHits.bigWin.amazing++;
      else if (feat === 'bigWinAwesome') this._featureHits.bigWin.awesome++;
      else if (feat === 'bigWinIncredible') this._featureHits.bigWin.incredible++;
    }

    // Recent spins ring buffer
    this._recentSpins.push(result);
    if (this._recentSpins.length > MAX_RECENT) {
      this._recentSpins.shift();
    }
  }

  /** Return a readonly snapshot of all session stats. */
  getStats(): Readonly<SessionStats> {
    const rtp = this._totalBet > 0 ? (this._totalWin / this._totalBet) * 100 : 0;
    const avgSpinTime =
      this._totalSpins > 1 ? this._spinTimeSum / (this._totalSpins - 1) : 0;

    return {
      totalSpins: this._totalSpins,
      totalBet: this._totalBet,
      totalWin: this._totalWin,
      rtp,
      featureHits: { ...this._featureHits, bigWin: { ...this._featureHits.bigWin } },
      winDistribution: { ...this._winDistribution },
      recentSpins: [...this._recentSpins],
      startTime: this._startTime,
      avgSpinTime,
    };
  }

  /** Reset all counters and start a fresh session. */
  reset(): void {
    this._totalSpins = 0;
    this._totalBet = 0;
    this._totalWin = 0;
    this._featureHits = emptyFeatureHits();
    this._winDistribution = emptyDistribution();
    this._recentSpins = [];
    this._startTime = Date.now();
    this._lastSpinTime = this._startTime;
    this._spinTimeSum = 0;
  }

  /** e.g. "96.2%" or "N/A" when no bets recorded. */
  getRTPString(): string {
    if (this._totalBet <= 0) return 'N/A';
    const rtp = (this._totalWin / this._totalBet) * 100;
    return `${rtp.toFixed(1)}%`;
  }

  /** e.g. "JP×2 RF×5 FG×1 BW×0" */
  getFeatureString(): string {
    const bw =
      this._featureHits.bigWin.amazing +
      this._featureHits.bigWin.awesome +
      this._featureHits.bigWin.incredible;
    return (
      `JP\u00d7${this._featureHits.jackpotPick} ` +
      `RF\u00d7${this._featureHits.rushFever} ` +
      `FG\u00d7${this._featureHits.freeGames} ` +
      `BW\u00d7${bw}`
    );
  }

  /** e.g. "0x:42 S:30 M:15 B:8 H:3 MG:2" */
  getDistributionString(): string {
    const d = this._winDistribution;
    return (
      `0x:${d.zero} S:${d.small} M:${d.medium} ` +
      `B:${d.big} H:${d.huge} MG:${d.mega}`
    );
  }
}
